/**
 * Phase 20 Plan 20-02 — NotificationsBell (live implementation).
 *
 * Replaces Phase 3 static placeholder.
 *
 * - Polls GET /api/alerts/unread-count every 30s (disabled when no notifications.view).
 * - Click opens dropdown with top 5 New alerts + "View all" link to /alerts.
 * - Per-row Acknowledge button (visible only with notifications.acknowledge).
 * - Empty state when no new alerts.
 *
 * Client feedback (2026-09): alerts opened from the bell showed no detail. The rows
 * now render the notification type and the full (multi-line) message, and clicking
 * one opens the same AlertDetailDialog the /alerts page uses — previously the only
 * route to an alert's details was navigating to /alerts and finding the row again.
 * The dropdown is closed before the dialog opens so the two Radix layers never
 * fight over focus.
 */

import { BellIcon } from '@phosphor-icons/react';
import { useState, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { AlertDetailDialog } from '@/components/notifications/AlertDetailDialog';
import { AlertRow } from '@/components/notifications/AlertRow';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAlertsQuery, useUnreadCountQuery } from '@/hooks/useNotificationsQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import type { AlertDto } from '@/types/notifications';

export function NotificationsBell(): JSX.Element {
  const { permissions } = useAuth();
  const hasView = permissions.includes('notifications.view');
  const hasAck = permissions.includes('notifications.acknowledge');

  // The dropdown is controlled so selecting a row can close it before the detail
  // dialog mounts — leaving both open traps focus inside the menu.
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [detailAlert, setDetailAlert] = useState<AlertDto | null>(null);

  const onSelectAlert = (alert: AlertDto): void => {
    setMenuOpen(false);
    setDetailAlert(alert);
  };

  const countQuery = useUnreadCountQuery({ enabled: hasView });
  const count = countQuery.data?.count ?? 0;

  // REVIEW FF-005: pass explicit `enabled` so the query truly does not fire
  // when the user lacks `notifications.view` (previous code still issued
  // a `GET /api/alerts?` for every authenticated user — backend 403).
  const alertsQuery = useAlertsQuery(
    { status: 'New', page: 1, pageSize: 5 },
    { enabled: hasView },
  );
  const alerts = alertsQuery.data?.items ?? [];

  // When user lacks view perm, render the disabled placeholder (Phase 3 behavior)
  if (!hasView) {
    return (
      <Button
        variant="ghost"
        size="icon"
        aria-label="Notifications (no permission)"
        title="You do not have permission to view notifications"
        disabled
        data-testid="notifications-bell"
      >
        <BellIcon size={18} weight="duotone" />
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Notifications"
            data-testid="notifications-bell"
            className="relative"
          >
            <BellIcon size={18} weight="duotone" />
            {count > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground"
                data-testid="notifications-unread-badge"
              >
                {count > 99 ? '99+' : String(count)}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          className="w-80"
          data-testid="notifications-dropdown"
        >
          <div className="px-3 py-2">
            <p className="text-sm font-semibold">Notifications</p>
          </div>
          <DropdownMenuSeparator />

          {alerts.length === 0 ? (
            <div className="px-3 py-4 text-center" role="status" aria-live="polite">
              <p className="text-sm text-muted-foreground">All caught up — no new alerts.</p>
            </div>
          ) : (
            <div>
              {alerts.map((alert) => (
                <AlertRow
                  key={alert.id}
                  alert={alert}
                  showAcknowledge={hasAck}
                  onSelect={onSelectAlert}
                />
              ))}
            </div>
          )}

          <DropdownMenuSeparator />
          <div className="px-3 py-2">
            <Link
              to="/alerts"
              className="text-sm text-primary hover:underline"
              data-testid="notifications-view-all-link"
            >
              View all alerts
            </Link>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {detailAlert !== null && (
        <AlertDetailDialog
          open
          alert={detailAlert}
          onClose={() => {
            setDetailAlert(null);
          }}
        />
      )}
    </>
  );
}
